/**
 * saleAllocations.ts — reconstruct which batches an invoice's stock came out of.
 *
 * A sale emits one batch-tagged `OUT` movement per FEFO allocation. Returning or
 * voiding must put those units back into the batches they came from, or
 * `batch.quantity` only ever falls while `product.currentStock` recovers — the
 * two projections drift apart permanently, and the server's event-derived batch
 * stock (`stockDerivation.batchStockDelta`) inherits the drift.
 *
 * "Outstanding draw" = what the sale took out, minus what earlier credit notes
 * or a void reversal already gave back. Netting matters: two partial returns
 * against one invoice would otherwise each plan against the original draw and
 * restore the same units twice.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { Invoice } from '@/db/models/Billing';
import { StockMovement } from '@/db/models/Inventory';
import { netRemainingDraw, type BatchAllocation } from './batchAllocation';

/** Movement types that put stock back. */
const RESTORE_TYPES = ['IN', 'VOID_REVERSAL'];

/** Every movement that has already given stock back for this sale + product. */
async function restoreMovementsForSale(
  saleInvoiceId: string,
  productId: string,
): Promise<StockMovement[]> {
  // A void reversal references the sale directly; a credit note's IN movements
  // reference the credit-note invoice, which points back via referenceInvoiceId.
  const creditNotes = await database.get<Invoice>('invoices').query(
    Q.where('reference_invoice_id', saleInvoiceId),
    Q.where('type', 'CREDIT_NOTE'),
    Q.where('is_deleted', false),
  ).fetch();

  return database.get<StockMovement>('stock_movements').query(
    Q.where('reference_id', Q.oneOf([saleInvoiceId, ...creditNotes.map((cn) => cn.id)])),
    Q.where('product_id', productId),
    Q.where('type', Q.oneOf(RESTORE_TYPES)),
    Q.where('is_deleted', false),
  ).fetch();
}

/**
 * Units of `productId` already given back against this sale, batch-tagged or not.
 *
 * Voiding an invoice that already carries a partial credit note must only reverse
 * what the sale *still* holds. Restoring the full line quantity would put back
 * more units than ever left the shelf.
 */
export async function restoredQtyForSale(
  saleInvoiceId: string,
  productId: string,
): Promise<number> {
  const movements = await restoreMovementsForSale(saleInvoiceId, productId);
  return movements.reduce((sum, m) => sum + Math.abs(m.quantity), 0);
}

function toAllocations(movements: StockMovement[]): BatchAllocation[] {
  return movements
    .filter((m) => m.batchId)
    .map((m) => ({ batchId: m.batchId as string, allocatedQty: Math.abs(m.quantity) }));
}

/**
 * The batch draw a sale still holds for one product, oldest allocation first.
 *
 * Untagged (product-level) movements are ignored — a sale made before batch
 * tagging existed has no batch to restore to, and the caller should put those
 * units back on `product.currentStock` alone.
 */
export async function outstandingBatchDraw(
  saleInvoiceId: string,
  productId: string,
): Promise<BatchAllocation[]> {
  const outMovements = await database.get<StockMovement>('stock_movements').query(
    Q.where('reference_id', saleInvoiceId),
    Q.where('product_id', productId),
    Q.where('type', 'OUT'),
    Q.where('is_deleted', false),
    Q.sortBy('created_at', Q.asc),
  ).fetch();

  const drawn = toAllocations(outMovements);
  if (drawn.length === 0) return [];

  const restored = await restoreMovementsForSale(saleInvoiceId, productId);
  return netRemainingDraw(drawn, toAllocations(restored));
}
